import * as React from "react";
import { useRef, useEffect, useId } from 'react';
import Swiper from 'swiper';
import { Pagination, Navigation, A11y, FreeMode } from 'swiper/modules';
import ImageContentCardLightSwiperSlide from "components/partials/parts/image-content-card-swiper-slide";
import { ReactComponent as CaretLeftIcon } from "img/arrows/caret-left.svg";
import { ReactComponent as CaretRightIcon } from "img/arrows/caret-right.svg";
import { IntroHeadlineProps } from "types/intro-headline-props";
import { ImageContentCardData } from "types/image-content-card-data";
import IntroHeadline from "components/partials/parts/intro-headline";

type CarouselCardsProps = {
    intro?: IntroHeadlineProps;
    patternModifierClass?: string;
    cards: ImageContentCardData[];
};

const CarouselCards: React.FC<CarouselCardsProps> = ({ patternModifierClass = 'bg-brand-red', cards, intro }) => {
    const swiperRef = useRef(null);

    // ensures unique id for each carousel
    const id = useId().replaceAll(':', '');

    useEffect(() => {
        if (swiperRef.current !== null) {
            const swiperInstance = new Swiper(swiperRef.current, {
                modules: [Pagination, Navigation, A11y, FreeMode],
                slidesPerView: 1,
                spaceBetween: 24,
                centerInsufficientSlides: false,
                freeMode: true,
                watchSlidesProgress: true,
                navigation: {
                    nextEl: `.carousel-wrapper-${id} .swiper-nav .swiper-nav-next`,
                    prevEl: `.carousel-wrapper-${id} .swiper-nav .swiper-nav-prev`,
                },
                pagination: {
                    el: `.carousel-wrapper-${id} .swiper-nav .swiper-pagination`,
                    type: 'bullets',
                    hideOnClick: false,
                    clickable: true,
                },
                breakpoints: {
                    650: {
                        slidesPerView: 2,
                        spaceBetween: 24,
                    },
                    900: {
                        slidesPerView: 3,
                        spaceBetween: 40,
                    },
                    1300: {
                        slidesPerView: 4,
                        spaceBetween: 40,
                    }

                }
            });

            if (process.env.GATSBY_SERVER_ENV === 'dev') {
                // Attach the swiper instance to the window object
                // for debugging purposes.
                // you will need to review the rendered html to find the correct id for 
                // carousel-wrapper-${id} and replace XXX with the id.
                // console commands:
                // window.swiperCarouselCardsXXX.slideNext();
                // window.swiperCarouselCardsXXX.slidePrev();
                (window as any)[`swiperCarouselCards${id}`] = swiperInstance;
            }

        }
    }, []);

    return (
        <div className={`pattern pattern--pad pattern--has-watermark pattern--carousel-cards pattern--has-bgcolor ${patternModifierClass}`}>
            <div className="outer-gutter">
                <div className="container">
                    {intro && <IntroHeadline {...intro} />}
                </div>
            </div>

            <div className={`carousel-wrapper carousel-wrapper-${id} container container--xxl`}>
                <div className="swiper js-carousel-slideshow" ref={swiperRef}>
                    <div className="swiper-wrapper">
                        {cards.map((card, i) => (
                            <ImageContentCardLightSwiperSlide key={i} {...card} />
                        ))}
                    </div>
                </div>
                <div className="swiper-nav">
                    <div className="swiper-nav-prev btn-circle-arrow"><CaretLeftIcon /></div>
                    <div className="swiper-pagination"></div>
                    <div className="swiper-nav-next btn-circle-arrow"><CaretRightIcon /></div>
                </div>
            </div>
        </div>
    );
};

export default CarouselCards;